import './style.scss';
import Swiper from 'swiper';
import 'swiper/swiper.scss';
import { EffectFade, Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/modules/effect-fade.css';
import { gsap } from "gsap";

export function product_details_block() {
    const block = document.querySelector('.product_details_block');
    if (!block) return;


    const swiper = new Swiper(block.querySelector('.product-details-swiper'), {
        slidesPerView: 1,
        spaceBetween: 40,
        loop: true,
        mousewheel: true,
        effect: 'fade',
        speed: 300,
        modules: [EffectFade, Autoplay, Navigation, Pagination],
        fadeEffect: { crossFade: true },
        // autoplay: {delay: 10000, disableOnInteraction: false},

        navigation: {
            nextEl: block.querySelector('.swiper-button-next'),
            prevEl: block.querySelector('.swiper-button-prev'),
        },

        pagination: {
            el: block.querySelector('.pagination-wrapper'),
            type: 'fraction',
            renderFraction: (currentClass, totalClass) => `
        <div class="gallery-pill">
          <span class="${currentClass}">1</span>
          <span class="sep"> / </span>
          <span class="${totalClass}">1</span>
        </div>
      `,
            formatFractionCurrent: (n) => String(n),
            formatFractionTotal: (n) => String(n),
        },
    });


    // Count only non-duplicate slides (works when loop:true)
    const getRealSlideCount = () => {
        // swiper.slides is an HTMLCollection/NodeList — convert to array
        return Array.from(swiper.slides).filter(sl => !sl.classList.contains('swiper-slide-duplicate')).length;
    };

    // Set the images-length element with the real count
    const lengthEl = block.querySelector('.images-length');
    if (lengthEl) {
        lengthEl.textContent = String(getRealSlideCount());
    }


    const plusButton = block.querySelector(".plus-button");
    const closeButton = block.querySelector(".close-wrapper");
    const wrapper = block.querySelector(".product-details-swiper-wrapper");
    if (plusButton && closeButton && wrapper) {
        // Initially hidden
        gsap.set(wrapper, { autoAlpha: 0, pointerEvents: "none" });

        plusButton.addEventListener("click", (e) => {
            if (getRealSlideCount() === 0 || plusButton.hasAttribute('disabled')) {
                e.preventDefault();
                return;
            }
            document.documentElement.classList.add("modal-opened");

            gsap.to(wrapper, {
                autoAlpha: 1,
                duration: 0.3,
                ease: "power2.out",
                pointerEvents: "auto",
            });
        });

        closeButton.addEventListener("click", () => {
            document.documentElement.classList.remove("modal-opened");

            gsap.to(wrapper, {
                autoAlpha: 0,
                duration: 0.3,
                ease: "power1.in",
                onComplete: () => {
                    gsap.set(wrapper, { pointerEvents: "none" });
                    block.querySelectorAll('.zoom-button.zoomed').forEach(btn => btn.click());
                },
            });
        });
    }


    block.querySelectorAll('.zoom-button').forEach(button => {

        const slide = button.closest('.swiper-slide');
        const img = slide?.querySelector('img');

        if (!img) return;

        let isZoomed = false;
        let scale = 2.5; 
        let currentX = 0;
        let currentY = 0;
        let startX = 0;
        let startY = 0;
        let isDragging = false;
        
        img.style.transition = 'none';

        const resetImage = () => {
            gsap.to(img, { x: 0, y: 0, scale: 1, duration: 0.4, ease: 'power2.out' });
            currentX = 0;
            currentY = 0;
            if (swiper) swiper.allowTouchMove = true;
        }

        const clamp = (val, min, max) => Math.max(min, Math.min(val, max));

        const getBounds = () => {
            const rectWidth = img.offsetWidth;
            const rectHeight = img.offsetHeight;
            const slideRect = slide.getBoundingClientRect();

            return {
                maxX: (rectWidth * scale - slideRect.width) / 2,
                maxY: (rectHeight * scale - slideRect.height) / 2
            }
        };

        const handleMouseMove = (e) => {
            if (!isZoomed || isDragging) return;
            
            const rect = slide.getBoundingClientRect();
            const xRatio = (e.clientX - rect.left) / rect.width - 0.5;
            const yRatio = (e.clientY - rect.top) / rect.height - 0.5;

            const bounds = getBounds();
            const maxPanX = Math.max(0, bounds.maxX);
            const maxPanY = Math.max(0, bounds.maxY);

            const targetX = -xRatio * maxPanX * 2; 
            const targetY = -yRatio * maxPanY * 2;

            gsap.to(img, { x: targetX, y: targetY, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
        };

        const handleTouchStart = (e) => {
            if (!isZoomed) return;
            isDragging = true;
            startX = e.touches[0].clientX - currentX;
            startY = e.touches[0].clientY - currentY;
        };

        const handleTouchMove = (e) => {
            if (!isDragging || !isZoomed) return;
            
            if (e.cancelable) {
                e.preventDefault(); 
            }
            
            let targetX = e.touches[0].clientX - startX;
            let targetY = e.touches[0].clientY - startY;

            const bounds = getBounds();
            const maxPanX = Math.max(0, bounds.maxX);
            const maxPanY = Math.max(0, bounds.maxY);

            targetX = clamp(targetX, -maxPanX, maxPanX);
            targetY = clamp(targetY, -maxPanY, maxPanY);

            currentX = targetX;
            currentY = targetY;

            gsap.set(img, { x: targetX, y: targetY });
        };

        const handleTouchEnd = () => {
            isDragging = false;
        };

        button.addEventListener('click', () => {
            isZoomed = !isZoomed;
            button.classList.toggle('zoomed', isZoomed);
            
            if (isZoomed) {
                if (swiper) swiper.allowTouchMove = false;
                img.style.cursor = 'grab';
                
                gsap.to(img, { scale: scale, duration: 0.4, ease: 'power2.out' });
                
                slide.addEventListener('mousemove', handleMouseMove);
                slide.addEventListener('touchstart', handleTouchStart);
                slide.addEventListener('touchmove', handleTouchMove, { passive: false });
                slide.addEventListener('touchend', handleTouchEnd);
                slide.addEventListener('mouseleave', () => {
                    if (!isDragging && isZoomed) {
                        gsap.to(img, { x: 0, y: 0, duration: 0.5, ease: 'power2.out' });
                    }
                });

            } else {
                img.style.cursor = 'default';
                resetImage();
                slide.removeEventListener('mousemove', handleMouseMove);
                slide.removeEventListener('touchstart', handleTouchStart);
                slide.removeEventListener('touchmove', handleTouchMove);
                slide.removeEventListener('touchend', handleTouchEnd);
            }
        });
    });

    if (swiper) {
        swiper.on('slideChange', () => {
            block.querySelectorAll('.zoom-button.zoomed').forEach(btn => btn.click());
        });
    }


    const addToQuote = block.querySelector(".add-to-quote");
    if (!addToQuote) return;

    const decreaseBtn = addToQuote.querySelector(".quantity-decrease");
    const increaseBtn = addToQuote.querySelector(".quantity-increase");
    const quantityNumber = addToQuote.querySelector(".quantity-number");

    if (!decreaseBtn || !increaseBtn || !quantityNumber) return;

    let quantity = parseInt(quantityNumber.textContent, 10) || 1;

    increaseBtn.addEventListener("click", () => {
        quantity++;
        quantityNumber.textContent = quantity;
    });

    decreaseBtn.addEventListener("click", () => {
        if (quantity > 1) {
            quantity--;
            quantityNumber.textContent = quantity;
        }
    });

}
